import { useState, useEffect, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  PlusCircle,
  ListFilter,
  Activity,
  FileDown,
  CheckCircle2,
  AlertCircle,
  Clock,
  CreditCard,
  UserPlus,
  Bell,
  Zap,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { PlatformKPIs } from "@platform/shared";

// ---------------------------------------------------------------------------
// Demo data — used as FALLBACK when the API is unavailable
// ---------------------------------------------------------------------------
const DEMO_KPIS: PlatformKPIs = {
  mrr: 1250000,           // ₪12,500
  arr: 15000000,          // ₪150,000
  churnRate: 2.3,
  netNewMrr: 35000,       // +₪350 this month
  activeCount: 47,
  suspendedCount: 2,
  totalRevenueThisMonth: 1250000,
};

// ---------------------------------------------------------------------------
// Demo activity feed
// ---------------------------------------------------------------------------
type ActivityType = "new_tenant" | "payment" | "alert" | "upgrade" | "health";

interface ActivityEvent {
  readonly id: string;
  readonly type: ActivityType;
  readonly description: string;
  readonly time: string;
}

const DEMO_ACTIVITY: ReadonlyArray<ActivityEvent> = [
  {
    id: "1",
    type: "new_tenant",
    description: 'דייר חדש נוצר: "Cafe Sheli"',
    time: "לפני 12 דקות",
  },
  {
    id: "2",
    type: "payment",
    description: "תשלום חודשי התקבל — ₪349 מ‑BurgerBar TLV",
    time: "לפני 43 דקות",
  },
  {
    id: "3",
    type: "upgrade",
    description: 'שדרוג תוכנית: "Pizza Roma" עלה ל‑Pro',
    time: "לפני 1 שעה",
  },
  {
    id: "4",
    type: "alert",
    description: 'אזהרה: שגיאת תשלום אצל "Sushi Nago"',
    time: "לפני 2 שעות",
  },
  {
    id: "5",
    type: "new_tenant",
    description: 'דייר חדש נוצר: "Ice Dream"',
    time: "לפני 3 שעות",
  },
  {
    id: "6",
    type: "health",
    description: "בדיקת בריאות עברה בהצלחה — כל השירותים פעילים",
    time: "לפני 4 שעות",
  },
];

// ---------------------------------------------------------------------------
// Quick actions
// ---------------------------------------------------------------------------
interface QuickAction {
  readonly label: string;
  readonly href: string;
  readonly icon: ReactNode;
  readonly color: string;
}

const QUICK_ACTIONS: ReadonlyArray<QuickAction> = [
  {
    label: "הקם דייר חדש",
    href: "/super/provisioning",
    icon: <PlusCircle className="h-6 w-6" />,
    color: "text-emerald-500",
  },
  {
    label: "צפה בדיירים",
    href: "/super/tenants",
    icon: <ListFilter className="h-6 w-6" />,
    color: "text-blue-500",
  },
  {
    label: "בדוק בריאות",
    href: "/super/health",
    icon: <Activity className="h-6 w-6" />,
    color: "text-amber-500",
  },
  {
    label: "ייצא דוח",
    href: "/super/billing",
    icon: <FileDown className="h-6 w-6" />,
    color: "text-purple-500",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatNIS(agorot: number): string {
  return `\u20AA${(agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function churnColor(rate: number): string {
  if (rate < 5) return "text-emerald-500";
  if (rate <= 10) return "text-amber-500";
  return "text-red-500";
}

// ---------------------------------------------------------------------------
// KPI Card Skeleton
// ---------------------------------------------------------------------------
function KPICardSkeleton(): ReactNode {
  return (
    <div className="relative overflow-hidden rounded-xl bg-card/80 border border-border/50 shadow-sm">
      <div className="absolute inset-y-0 start-0 w-1 rounded-s-xl bg-muted" />
      <div className="relative px-5 pt-4 pb-4 ps-6 space-y-3 animate-pulse">
        <div className="flex items-start justify-between gap-3">
          <div className="h-3 w-16 rounded bg-muted" />
          <div className="h-9 w-9 rounded-lg bg-muted" />
        </div>
        <div className="h-8 w-24 rounded bg-muted" />
        <div className="h-3 w-20 rounded bg-muted" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
interface KPICardProps {
  readonly title: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly icon: ReactNode;
  readonly trend?: "up" | "down" | "neutral";
  readonly trendValue?: string;
  readonly accentColor: string;
  readonly valueColorClass?: string;
}

function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  accentColor,
  valueColorClass,
}: KPICardProps): ReactNode {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-xl",
        "bg-card/80 backdrop-blur-sm",
        "border border-border/50",
        "shadow-sm hover:shadow-md",
        "transition-all duration-200",
        "group",
      ].join(" ")}
    >
      {/* Left accent bar */}
      <div
        className="absolute inset-y-0 start-0 w-1 rounded-s-xl"
        style={{ background: accentColor }}
      />

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-muted/10 pointer-events-none" />

      <div className="relative px-5 pt-4 pb-4 ps-6">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground leading-tight">
            {title}
          </p>
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-110"
            style={{ background: `${accentColor}18` }}
          >
            <span style={{ color: accentColor }}>{icon}</span>
          </div>
        </div>

        {/* Value */}
        <p
          className={[
            "text-3xl font-bold tabular-nums leading-none mb-1.5",
            valueColorClass ?? "text-foreground",
          ].join(" ")}
        >
          {value}
        </p>

        {/* Trend / subtitle */}
        {(trendValue ?? subtitle) && (
          <div className="flex items-center gap-1 text-xs">
            {trend === "up" && (
              <span className="flex items-center gap-0.5 text-emerald-500 font-medium">
                <TrendingUp className="h-3.5 w-3.5" />
                {trendValue}
              </span>
            )}
            {trend === "down" && (
              <span className="flex items-center gap-0.5 text-red-500 font-medium">
                <TrendingDown className="h-3.5 w-3.5" />
                {trendValue}
              </span>
            )}
            {(!trend || trend === "neutral") && (
              <span className="text-muted-foreground">{trendValue ?? subtitle}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Action Card
// ---------------------------------------------------------------------------
function QuickActionCard({ action }: { readonly action: QuickAction }): ReactNode {
  return (
    <NavLink
      to={action.href}
      className={[
        "flex flex-col items-center justify-center gap-2.5",
        "min-h-[88px] rounded-xl p-4",
        "bg-card/80 backdrop-blur-sm",
        "border border-border/50",
        "shadow-sm",
        "hover:shadow-md hover:scale-[1.03] hover:border-border",
        "active:scale-[0.98]",
        "transition-all duration-200",
        "cursor-pointer group",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-11 w-11 items-center justify-center rounded-xl",
          "bg-muted/60 group-hover:bg-muted transition-colors duration-200",
          action.color,
        ].join(" ")}
      >
        {action.icon}
      </span>
      <span className="text-xs font-semibold text-center text-foreground leading-tight">
        {action.label}
      </span>
    </NavLink>
  );
}

// ---------------------------------------------------------------------------
// Activity icon
// ---------------------------------------------------------------------------
function ActivityIcon({ type }: { readonly type: ActivityType }): ReactNode {
  switch (type) {
    case "new_tenant":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
          <UserPlus className="h-4 w-4 text-emerald-500" />
        </span>
      );
    case "payment":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/15">
          <CreditCard className="h-4 w-4 text-blue-500" />
        </span>
      );
    case "upgrade":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-500/15">
          <Zap className="h-4 w-4 text-purple-500" />
        </span>
      );
    case "alert":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-500/15">
          <Bell className="h-4 w-4 text-red-500" />
        </span>
      );
    case "health":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Activity badge
// ---------------------------------------------------------------------------
function ActivityBadge({ type }: { readonly type: ActivityType }): ReactNode {
  switch (type) {
    case "new_tenant":
      return <Badge variant="success">דייר חדש</Badge>;
    case "payment":
      return <Badge variant="info">תשלום</Badge>;
    case "upgrade":
      return <Badge variant="default">שדרוג</Badge>;
    case "alert":
      return <Badge variant="danger">התראה</Badge>;
    case "health":
      return <Badge variant="success">בריאות</Badge>;
  }
}

// ---------------------------------------------------------------------------
// System Status
// ---------------------------------------------------------------------------
interface StatusItemProps {
  readonly label: string;
  readonly healthy: boolean;
}

function StatusItem({ label, healthy }: StatusItemProps): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={[
          "h-2 w-2 rounded-full",
          healthy
            ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"
            : "bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.6)]",
        ].join(" ")}
      />
      <span
        className={[
          "text-xs font-medium",
          healthy ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function SuperDashboardPage(): ReactNode {
  const [kpis, setKpis] = useState<PlatformKPIs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDemo, setUsingDemo] = useState(false);

  const fetchKpis = () => {
    setLoading(true);
    setError(null);

    fetch("/super/api/kpis", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: PlatformKPIs }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setKpis(json.data);
        setUsingDemo(false);
      })
      .catch(() => {
        // Fall back to demo data silently — show warning badge
        setKpis(DEMO_KPIS);
        setUsingDemo(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchKpis();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString("he-IL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Resolved KPIs — either live or demo fallback
  const data = kpis ?? DEMO_KPIS;

  return (
    <div className="space-y-6 p-4 md:p-6" dir="rtl">
      {/* ── Page header ── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            דשבורד פלטפורמה
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{dateStr}</p>
        </div>
        {usingDemo ? (
          <Badge variant="warning" className="gap-1.5 px-3 py-1 text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 inline-block" />
            נתוני דמו — שרת לא זמין
          </Badge>
        ) : !loading && (
          <Badge variant="info" className="gap-1.5 px-3 py-1 text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 inline-block" />
            נתונים בזמן אמת
          </Badge>
        )}
      </div>

      {/* ── Error banner with retry ── */}
      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <span className="text-sm text-destructive flex-1">{error}</span>
          <Button size="sm" variant="outline" onClick={fetchKpis} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" />
            נסה שנית
          </Button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {loading ? (
          <>
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
            <KPICardSkeleton />
          </>
        ) : (
          <>
            <KPICard
              title="MRR"
              value={formatNIS(data.mrr)}
              icon={<DollarSign className="h-4 w-4" />}
              trend={data.netNewMrr > 0 ? "up" : data.netNewMrr < 0 ? "down" : "neutral"}
              {...(data.netNewMrr !== 0
                ? {
                    trendValue: `${data.netNewMrr > 0 ? "+" : ""}${formatNIS(data.netNewMrr)} החודש`,
                  }
                : {})}
              accentColor="#10b981"
            />
            <KPICard
              title="ARR"
              value={formatNIS(data.arr)}
              icon={<TrendingUp className="h-4 w-4" />}
              subtitle="MRR × 12"
              trend="neutral"
              accentColor="#6366f1"
            />
            <KPICard
              title="שיעור נטישה"
              value={`${data.churnRate.toFixed(1)}%`}
              icon={<TrendingDown className="h-4 w-4" />}
              subtitle={data.churnRate < 5 ? "טוב מאוד" : data.churnRate <= 10 ? "ממוצע" : "גבוה"}
              accentColor="#ef4444"
              valueColorClass={churnColor(data.churnRate)}
            />
            <KPICard
              title="דיירים פעילים"
              value={String(data.activeCount)}
              icon={<Users className="h-4 w-4" />}
              subtitle={
                data.suspendedCount > 0
                  ? `${data.suspendedCount} מושהים`
                  : "הכל תקין"
              }
              trend="neutral"
              accentColor="#3b82f6"
            />
          </>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          פעולות מהירות
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {QUICK_ACTIONS.map((action) => (
            <QuickActionCard key={action.href} action={action} />
          ))}
        </div>
      </div>

      {/* ── Bottom: Activity + Status ── */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Activity feed — takes 2 of 3 columns */}
        <Card className="md:col-span-2 border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">
                פעילות אחרונה
              </CardTitle>
              <Badge variant="secondary" className="text-xs">
                {DEMO_ACTIVITY.length} אירועים
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-0">
              {DEMO_ACTIVITY.map((event, idx) => (
                <div key={event.id}>
                  <div className="flex items-start gap-3 py-3">
                    {/* Timeline dot + line */}
                    <div className="flex flex-col items-center gap-0 shrink-0">
                      <ActivityIcon type={event.type} />
                      {idx < DEMO_ACTIVITY.length - 1 && (
                        <div className="w-px h-3 bg-border/50 mt-1" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <p className="text-sm text-foreground leading-snug">
                        {event.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground">{event.time}</span>
                        <ActivityBadge type={event.type} />
                      </div>
                    </div>
                  </div>
                  {idx < DEMO_ACTIVITY.length - 1 && (
                    <Separator className="opacity-40" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right column: stats + status */}
        <div className="flex flex-col gap-4">
          {/* Mini stats */}
          <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">סטטיסטיקות</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">גידול חודשי</span>
                <span className="text-sm font-bold text-emerald-500">+3 דיירים</span>
              </div>
              <Separator className="opacity-40" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">הכנסות החודש</span>
                <span className="text-sm font-bold tabular-nums">{formatNIS(data.totalRevenueThisMonth)}</span>
              </div>
              <Separator className="opacity-40" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">ARPU</span>
                <span className="text-sm font-bold tabular-nums">
                  {data.activeCount > 0
                    ? formatNIS(Math.round(data.mrr / data.activeCount))
                    : "—"}
                </span>
              </div>
              <Separator className="opacity-40" />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">דיירים מושהים</span>
                <span className="text-sm font-bold text-amber-500">{data.suspendedCount}</span>
              </div>
            </CardContent>
          </Card>

          {/* System status */}
          <Card className="border-border/50 bg-card/80 backdrop-blur-sm shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                סטטוס מערכת
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                <StatusItem label="API" healthy={true} />
                <StatusItem label="DB" healthy={true} />
                <StatusItem label="Workers" healthy={true} />
                <StatusItem label="Queues" healthy={true} />
                <StatusItem label="CDN" healthy={true} />
                <StatusItem label="Email" healthy={false} />
              </div>
              <div className="mt-3 pt-3 border-t border-border/40">
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-[11px] text-muted-foreground">
                    Email: השהייה גבוהה — בדיקה פעילה
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
